export { STAFF_SCOPE } from "./navigation.ts";

import type { WorkforceMember } from "@tdelivr/api-client/generated/identity/workforce-identity.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the staff board does to what IAM-02 returns, away from any rendering.
 *
 * Built to `22 WP21 Merchant Portal.dc.html`, page `staff`:
 *
 * ```
 * staff:['Staff','IAM-02','Who at your shop can accept orders or change the menu.',
 *   [['Staff','8','done',''],['Order takers','6','done',''],['Managers','2','done',''],
 *    ['Invites pending','1','waiting','']],
 *   ['Person','Role','Branch'],
 *   [['Grace Njeri','Manager','Kilimani','done','active'],
 *    ['Tom Otieno','Order taker','Kilimani','done','active'],
 *    ['Esther Wairimu','Order taker','Westlands','waiting','invited']],
 *   'info','Only managers can change prices or stock counts.']
 * ```
 *
 * ## This page's recorded blocker was right and has been closed by adding the thing it named
 *
 * It read: _"Nothing on the platform models a staff member: no name, no role, no branch. IAM-02
 * holds identity providers and subject links, which is how a person signs in rather than who they
 * are — and the design's three columns are all three of those fields. **This is a resource that
 * does not exist, not a route that is not published.**"_
 *
 * That last sentence is why this is the only one of the portal's three blocked pages that could not
 * have been served in the cohort that published three listings for it.
 * [ADR 0084](../../../docs/adr/0084-a-staff-member-is-workforce-identity-and-nothing-held-one.md)
 * adds `workforce_members` to IAM-02 and the listing over it.
 *
 * ## The design's own rows are what decided the shape of the table
 *
 * **Three rows across two branches — `Kilimani`, `Kilimani`, `Westlands` — so this board is one
 * shop's whole staff and not one branch's.** That was read off the design rather than assumed, and
 * the first reading was wrong: a table carrying only a branch could not have drawn it, because
 * working out which branches belong to this shop is MER-02's knowledge. Hence `merchantId` on the
 * record, and this board reads it from the token and sends it as a filter.
 *
 * ## What is drawn from data and what is not
 *
 * | drawn                            | source                                                        |
 * | -------------------------------- | ------------------------------------------------------------- |
 * | the rows                         | IAM-02 `GET /v1/tenants/{tenantId}/workforce-members`         |
 * | `Person` — `Grace Njeri`         | `displayName`                                                 |
 * | `Role` — `Manager`               | `role`, the tenant's own words                                 |
 * | `Branch` — `Kilimani`            | `locationId`. **The name is MER-02's** — see below             |
 * | `done · active`, `waiting · invited` | `status`. **Both of the design's two states are published** |
 * | the four tiles                   | no listing publishes a total (R-04); no tile row here         |
 * | the primary command              | drawn and refused — `INVITE_REFUSAL`                          |
 *
 * **The branch name is this board's one absence, and it is the same one the products board has for
 * a category.** A branch is a MER-02 location and its name lives there; joining it would be one
 * request per distinct branch, which is the fan-out Document 17 WEB-0743 tolerates for one listing
 * per screen and not for a column. So the cell draws the identifier in Geist Mono — the design's
 * own treatment for an identifier standing where a name belongs — and says why on its title.
 *
 * **`suspended` is not in this design and is drawn anyway.** IAM-02 publishes three states and the
 * merchant design draws two; a suspended member of this shop would otherwise appear with no badge
 * at all, which is worse than a word the design did not anticipate. The tenant console's own board
 * draws the same three, and `closed · suspended` is its word.
 */

/**
 * The state class and the word.
 *
 * `active` and `invited` are the design's own two, from its rows. `suspended` is IAM-02's third and
 * the tenant console's word for it, used here because a member in that state has to be shown
 * something.
 */
export function stateOf(member: WorkforceMember): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  switch (member.status) {
    case "active":
      return { classKey: "done", label: "active" };
    case "invited":
      return {
        classKey: "waiting",
        label: "invited",
        title:
          "A name and a role are recorded and no account is linked yet. This person cannot sign in until they do.",
      };
    case "suspended":
      return {
        classKey: "closed",
        label: "suspended",
        /*
         * The one badge on this board that needs a sentence, because it is the one word the design
         * does not draw: a merchant seeing it has no row in their own design to compare it with.
         */
        title:
          "This person can no longer sign in. IAM-02 publishes no way back from suspended, so taking them on again is a new invitation. The merchant portal's design draws two states and this is IAM-02's third.",
      };
  }
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(members: readonly WorkforceMember[]): readonly string[] {
  return [...new Set(members.map((member) => stateOf(member).label))];
}

/** One row of the staff board. */
export function staffRow(member: WorkforceMember): BoardRow {
  return {
    id: member.id,
    cells: [
      { text: member.displayName },
      { text: member.role },
      member.locationId === undefined
        ? {
            /*
             * Not an absence: a real emptiness, and a meaningful one. A member of this shop with
             * no branch works for the shop rather than at one of its counters — the design's own
             * `Manager` row is the case that could go either way, and the table allows both.
             */
            text: "—",
            title:
              "This person works for the shop rather than at one branch. IAM-02 records a branch only where there is one.",
          }
        : {
            text: member.locationId,
            mono: true,
            title: `This is the branch identifier. The name — the design's Kilimani — is MER-02's, and reading it would be one request per branch on a column. Branch ${member.locationId}.`,
          },
    ],
    state: stateOf(member),
    detail: [
      { label: "Member", value: member.id, mono: true },
      { label: "Role", value: member.role },
      {
        label: "Branch",
        value: member.locationId ?? "—",
        ...(member.locationId === undefined ? {} : { mono: true }),
      },
      {
        label: "Signs in as",
        value: member.platformSubject ?? "—",
        ...(member.platformSubject === undefined ? {} : { mono: true }),
      },
    ],
    history: [
      { label: "Invited", value: asDay(member.invitedAt) },
      {
        label: "Activated",
        value: member.activatedAt === undefined ? "—" : asDay(member.activatedAt),
      },
      { label: "Version", value: `v${String(member.version)}` },
    ],
    glyph: "person",
  };
}

/**
 * Why the designed action cannot be sent, in the words on the refused button.
 *
 * The design's `FORMS.staff` is five controls — `Profile photo (file, Optional) · Full name (text)
 * · Mobile (tel) · Role (select) · Branch (select)` — and **three of the five are fillable**, which
 * makes this the closest any refused command on this portal comes to being sendable. IAM-02's
 * invitation body takes a name, a role and a place.
 *
 * Two things stop it. This portal asks for no write scope at all, because every one of its primary
 * actions is refused (ADR 0032), and the photo and the mobile have nowhere on this platform to be
 * stored — no operation anywhere accepts an image, and an invitation this platform can deliver
 * needs a channel to deliver it on. The second is the one that survives a scope being granted.
 */
export const INVITE_REFUSAL =
  "This portal holds no write scope. IAM-02's invitation takes a name, a role and a branch — the designed form's profile photo and mobile have no store and no delivery channel on this platform — so staff are invited from the tenant admin console.";

/**
 * The closing note, replaced, keeping the design's kind and its place.
 *
 * The design writes `Only managers can change prices or stock counts.` That is a safety claim about
 * a control that does not exist: a member's `role` is words the tenant wrote (ADR 0084 §2), nothing
 * reads it when a price or a count is written, and IAM-03 — which would hold such a rule — is left
 * unwired by ADR 0016. A manager who believes an order taker cannot change a price has been told
 * something this platform does not enforce, which is the same class of promise the tenant console's
 * roles board removed about sensitive permissions needing a second person.
 */
export const CLOSING_NOTE =
  "A person's role here is a label rather than a permission: nothing on this platform checks it when a price or a stock count is written. What a person may do is decided by the scopes on their token.";

/**
 * The sentence under the grid, which exists because this board's rows are somebody else's to add.
 *
 * Worth saying out loud rather than leaving a merchant to infer it from a disabled button: the
 * listing is a merchant-filtered read of a **tenant's** staff record, so a shop sees its own people
 * and the tenant administrator is who adds them.
 */
export const LISTING_NOTE =
  "These are the people recorded against your shop. Staff records belong to the tenant, so a new person is invited from the tenant admin console and appears here once they are.";
